"use client"

import * as React from "react"

import { Button } from "@/components/ui/button"
import {
  CircleHelpIcon,
  type CircleHelpIconHandle,
} from "@/components/ui/circle-help"
import { cn } from "@/lib/utils"

type NeedHelpButtonProps = {
  href: string
  className?: string
  /** Icon square only (e.g. dashboard mobile header). */
  iconOnly?: boolean
  /** Visible label when not icon-only. Defaults to "Need Help?". */
  label?: string
}

/**
 * Contextual help centre CTA for inset breadcrumb toolbars —
 * Tropical Surf outline + animated circle-help icon.
 */
export function NeedHelpButton({
  href,
  className,
  iconOnly = false,
  label = "Need Help?",
}: NeedHelpButtonProps): React.ReactElement {
  const iconRef = React.useRef<CircleHelpIconHandle | null>(null)

  const startIconAnimation = React.useCallback(() => {
    void iconRef.current?.startAnimation()
  }, [])

  const stopIconAnimation = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])

  const iconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  )

  return (
    <Button
      asChild
      variant="outline-brand"
      size={iconOnly ? "icon" : "sm"}
      data-font="primary"
      data-need-help-button=""
      className={cn(iconOnly ? "h-8 w-8 shrink-0" : "shrink-0", className)}
      {...iconHoverHandlers}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
      >
        <CircleHelpIcon
          ref={iconRef}
          data-icon={iconOnly ? undefined : "inline-start"}
          size={16}
          className="pointer-events-none shrink-0 text-current"
          aria-hidden
        />
        {iconOnly ? null : label}
      </a>
    </Button>
  )
}
